import { useState, useRef, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Search, Check, FileText, Eye, Printer, Send, History, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { STANDARD_TEMPLATES, TEMPLATE_CATEGORIES, getCategoryMeta, fillTemplate, resolveVariables, buildPrintDocument } from '@/lib/documentTemplates';
import { useEmployees } from '@/api/queries/employee.queries';
import { useCompanySettings } from '@/api/queries/settings.queries';
import { useIssuedDocuments, useIssueDocument } from '@/api/queries/document.queries';
import { selectCompany } from '@/store/slices/companySlice';
import { selectUser } from '@/store/slices/authSlice';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { format } from 'date-fns';
import { toast } from 'sonner';

function EmployeeSelector({ selected, onSelect }) {
  const [search, setSearch] = useState('');
  const { data } = useEmployees({ limit: 200 });
  const employees = data?.data || [];

  const filtered = useMemo(() =>
    employees.filter(e => {
      const q = search.toLowerCase();
      const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
      return !q || name.includes(q) || (e.employee_code || '').toLowerCase().includes(q);
    }), [employees, search]);

  const deptStr = (d) => typeof d === 'string' ? d : (d?.name || '');
  const desigStr = (d) => typeof d === 'string' ? d : (d?.name || '');

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search employees…"
          className="w-full h-8 pl-8 pr-3 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-[8px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent)]" />
      </div>
      <div className="flex-1 overflow-y-auto space-y-0.5">
        {filtered.map(emp => {
          const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name;
          const isActive = selected?.id === emp.id;
          return (
            <button key={emp.id} onClick={() => onSelect(emp)}
              className={cn(
                'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[8px] text-left transition-all',
                isActive ? 'bg-[var(--accent-muted)] border border-[var(--accent)]/30' : 'hover:bg-[var(--bg-hover)]',
              )}>
              <Avatar name={name} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-[var(--text-primary)] truncate">{name}</p>
                <p className="text-[10px] text-[var(--text-tertiary)] truncate">{emp.employee_code} · {desigStr(emp.designation)} · {deptStr(emp.department)}</p>
              </div>
              {isActive && <Check size={13} className="text-[var(--accent)] shrink-0" />}
            </button>
          );
        })}
        {filtered.length === 0 && <p className="text-[12px] text-[var(--text-tertiary)] text-center py-6">No employees found</p>}
      </div>
    </div>
  );
}

function TemplateSelector({ selected, onSelect }) {
  const [activeCategory, setActiveCategory] = useState('ALL');
  const { data: settingsData } = useCompanySettings();
  const customTemplates = settingsData?.data?.settings?.document_templates || [];
  const allTemplates = useMemo(() => [...STANDARD_TEMPLATES, ...customTemplates], [customTemplates]);

  const filtered = useMemo(() =>
    allTemplates.filter(t => activeCategory === 'ALL' || t.category === activeCategory),
    [allTemplates, activeCategory]);

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex gap-1 flex-wrap">
        {[{ code: 'ALL', label: 'All' }, ...TEMPLATE_CATEGORIES].map(c => (
          <button key={c.code} onClick={() => setActiveCategory(c.code)}
            className={cn('h-6 px-2.5 text-[11px] font-medium rounded-full transition-all whitespace-nowrap',
              activeCategory === c.code ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] hover:bg-[var(--bg-app)]')}>
            {c.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto space-y-1.5">
        {filtered.map(tpl => {
          const cat = getCategoryMeta(tpl.category);
          const isActive = selected?.id === tpl.id;
          return (
            <button key={tpl.id} onClick={() => onSelect(tpl)}
              className={cn(
                'w-full flex items-start gap-2.5 p-2.5 rounded-[8px] text-left transition-all border',
                isActive ? 'bg-[var(--accent-muted)] border-[var(--accent)]/40' : 'border-transparent hover:bg-[var(--bg-hover)]',
              )}>
              <div className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center mt-0.5"
                style={{ background: cat.bg }}>
                <FileText size={13} style={{ color: cat.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-[var(--text-primary)] truncate">{tpl.name}</p>
                <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5 line-clamp-1">{tpl.description}</p>
              </div>
              {isActive && <Check size={13} className="text-[var(--accent)] shrink-0 mt-1" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DocumentEditor({ employee, template, company, user }) {
  const docRef    = useRef(null); // the contentEditable document div
  const hasEdited = useRef(false); // track if user has manually edited the doc
  const [overrides, setOverrides] = useState({});
  const issueDoc = useIssueDocument();

  const vars   = useMemo(() => resolveVariables(company, employee, user, overrides), [company, employee, user, overrides]);
  const filled = useMemo(() => fillTemplate(template.content_html, vars), [template, vars]);

  // Find unfilled vars from the BASE template (before overrides), not from filled
  const baseFilled = useMemo(() => fillTemplate(template.content_html, resolveVariables(company, employee, user, {})), [template, company, employee, user]);
  const unfilledVars = useMemo(() => {
    const matches = [];
    const re = /\{\{(\w+)\}\}/g;
    let m;
    while ((m = re.exec(baseFilled)) !== null) {
      if (!matches.includes(m[1])) matches.push(m[1]);
    }
    return matches;
  }, [baseFilled]);

  // Sync innerHTML when filled changes — but only if user hasn't manually edited
  useEffect(() => {
    if (!hasEdited.current && docRef.current) {
      docRef.current.innerHTML = filled;
    }
  }, [filled]);

  // On mount: set initial content
  useEffect(() => {
    if (docRef.current) {
      docRef.current.innerHTML = filled;
      hasEdited.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally only on mount

  const getCurrentHtml = () => docRef.current?.innerHTML || filled;

  const handleOverrideChange = (key, val) => {
    setOverrides(prev => ({ ...prev, [key]: val }));
    // Also do a live find-replace in the editable doc
    if (docRef.current) {
      const prev = docRef.current.innerHTML;
      // Replace remaining {{key}} in the editable doc
      const next = prev.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val || `{{${key}}}`);
      docRef.current.innerHTML = next;
    }
  };

  const handlePrint = () => {
    const html   = getCurrentHtml();
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;';
    document.body.appendChild(iframe);
    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(buildPrintDocument(html, template.name));
    iframe.contentWindow.document.close();
    iframe.contentWindow.focus();
    setTimeout(() => { iframe.contentWindow.print(); document.body.removeChild(iframe); }, 500);
  };

  const handleIssue = () => {
    const empName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.name;
    issueDoc.mutate({
      employee_id:   employee.id,
      employee_name: empName,
      employee_code: employee.employee_code,
      department:    typeof employee.department === 'string' ? employee.department : (employee.department?.name || ''),
      designation:   typeof employee.designation === 'string' ? employee.designation : (employee.designation?.name || ''),
      template_id:   template.id,
      template_name: template.name,
      content_html:  getCurrentHtml(),
      issued_by:     user?.name || 'HR',
      status:        'ISSUED',
    }, {
      onSuccess: () => toast.success(`Document issued to ${empName}`),
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Unfilled variable inputs */}
      {unfilledVars.length > 0 && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 shrink-0">
          <p className="text-[11px] font-semibold text-amber-700 mb-1">Fill remaining fields — these will update the document automatically:</p>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {unfilledVars.map(v => (
              <div key={v} className="flex items-center gap-1.5">
                <label className="text-[10px] font-mono text-amber-600">{v.replace(/_/g, ' ')}</label>
                <input
                  value={overrides[v] || ''}
                  onChange={e => handleOverrideChange(v, e.target.value)}
                  placeholder={v.replace(/_/g, ' ')}
                  className="h-6 px-2 text-[11px] bg-white border border-amber-300 rounded-[5px] focus:outline-none focus:border-amber-500 min-w-[120px]"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editable hint banner */}
      <div className="px-4 py-1.5 bg-blue-50 border-b border-blue-100 shrink-0 flex items-center gap-2">
        <Eye size={11} className="text-blue-500" />
        <p className="text-[10px] text-blue-600">Click anywhere in the document below to edit text directly before issuing.</p>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--border)] bg-[var(--bg-card)] shrink-0">
        <span className="text-[12px] text-[var(--text-tertiary)] flex-1 truncate">{template.name} → {`${employee.first_name || ''} ${employee.last_name || ''}`.trim()}</span>
        <button onClick={handlePrint}
          className="flex items-center gap-1.5 h-7 px-3 text-[11px] font-medium border border-[var(--border)] rounded-[7px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
          <Printer size={12}/> Print
        </button>
        <button onClick={handleIssue} disabled={issueDoc.isPending}
          className="flex items-center gap-1.5 h-7 px-3 text-[11px] font-medium bg-[var(--accent)] text-white rounded-[7px] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors">
          <Send size={12}/> {issueDoc.isPending ? 'Issuing…' : 'Issue Document'}
        </button>
      </div>

      {/* Document — contentEditable so user can make final edits */}
      <div className="flex-1 overflow-auto bg-[var(--bg-app)] p-5">
        <div className="max-w-[720px] mx-auto bg-white shadow-md rounded-lg overflow-hidden">
          <div
            ref={docRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => { hasEdited.current = true; }}
            style={{ outline: 'none', minHeight: '400px', cursor: 'text' }}
          />
        </div>
      </div>
    </div>
  );
}

function IssuedHistoryTable() {
  const { data } = useIssuedDocuments();
  const rows     = data?.data || [];

  if (!rows.length) return (
    <div className="flex flex-col items-center py-10 gap-2 text-[var(--text-tertiary)]">
      <History size={28} />
      <p className="text-[13px]">No documents issued yet</p>
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-[var(--border)]">
            {['Employee', 'Template', 'Issued On', 'Issued By', 'Status'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors">
              <td className="px-4 py-2.5">
                <p className="font-medium text-[var(--text-primary)]">{r.employee_name}</p>
                <p className="text-[var(--text-tertiary)] text-[10px]">{r.employee_code}</p>
              </td>
              <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.template_name}</td>
              <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.issued_date ? format(new Date(r.issued_date), 'dd MMM yyyy') : '—'}</td>
              <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.issued_by}</td>
              <td className="px-4 py-2.5">
                <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium',
                  r.status === 'ACKNOWLEDGED' ? 'bg-green-100 text-green-700' :
                  r.status === 'ISSUED' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600')}>
                  {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function IssueDocumentPage() {
  const company  = useSelector(selectCompany);
  const user     = useSelector(selectUser);
  const [step, setStep]           = useState(1); // 1=employee, 2=template, 3=document
  const [selEmployee, setEmployee] = useState(null);
  const [selTemplate, setTemplate] = useState(null);
  const [showHistory, setHistory]  = useState(false);

  const canNext1 = !!selEmployee;
  const canNext2 = !!selTemplate;

  const handleSelectEmployee = (emp) => { setEmployee(emp); };
  const handleSelectTemplate = (tpl) => { setTemplate(tpl); };
  const goNext = () => { if (step === 1 && canNext1) setStep(2); else if (step === 2 && canNext2) setStep(3); };
  const goBack = () => { if (step > 1) setStep(s => s - 1); };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[var(--border)] bg-[var(--bg-card)] shrink-0">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-[18px] font-bold text-[var(--text-primary)]">Issue Document</h1>
            <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">Select an employee and a template to generate and issue a document</p>
          </div>
          <button onClick={() => setHistory(v => !v)}
            className={cn('flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium rounded-[8px] border transition-colors',
              showHistory ? 'bg-[var(--accent-muted)] text-[var(--accent)] border-[var(--accent)]/30' : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
            <History size={13} /> {showHistory ? 'Close History' : 'Issue History'}
          </button>
        </div>

        {/* Step progress */}
        {!showHistory && (
          <div className="flex items-center gap-2 mt-3">
            {[{ n: 1, label: 'Select Employee' }, { n: 2, label: 'Select Template' }, { n: 3, label: 'Fill & Issue' }].map((s, i) => (
              <div key={s.n} className="flex items-center gap-2">
                <button onClick={() => s.n < step && setStep(s.n)}
                  className={cn('flex items-center gap-2 h-7 px-3 rounded-full text-[11px] font-medium transition-all',
                    step === s.n ? 'bg-[var(--accent)] text-white' :
                    step > s.n ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200' : 'bg-[var(--bg-hover)] text-[var(--text-tertiary)]')}>
                  <span className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                    style={{ background: step > s.n ? '#dcfce7' : 'transparent' }}>
                    {step > s.n ? '✓' : s.n}
                  </span>
                  {s.label}
                </button>
                {i < 2 && <ChevronRight size={12} className="text-[var(--text-tertiary)]" />}
              </div>
            ))}
          </div>
        )}
      </div>

      {showHistory ? (
        <div className="flex-1 overflow-auto p-5">
          <div className="card p-0 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-[var(--border)]">
              <h2 className="text-[14px] font-semibold text-[var(--text-primary)]">Issued Documents History</h2>
            </div>
            <IssuedHistoryTable />
          </div>
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* Step panels */}
          {step === 1 && (
            <div className="flex flex-col flex-1 max-w-sm border-r border-[var(--border)] bg-[var(--bg-card)] p-3">
              <p className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-2 px-1">Step 1 — Select Employee</p>
              <EmployeeSelector selected={selEmployee} onSelect={handleSelectEmployee} />
              {canNext1 && (
                <button onClick={goNext}
                  className="mt-3 h-9 bg-[var(--accent)] text-white text-[13px] font-medium rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors flex items-center justify-center gap-2 shrink-0">
                  Continue to Template <ChevronRight size={14} />
                </button>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col w-72 border-r border-[var(--border)] bg-[var(--bg-card)] p-3 shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <button onClick={goBack} className="text-[11px] text-[var(--accent)] hover:underline">← Back</button>
                <p className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">Step 2 — Select Template</p>
              </div>
              <TemplateSelector selected={selTemplate} onSelect={handleSelectTemplate} />
              {canNext2 && (
                <button onClick={goNext}
                  className="mt-3 h-9 bg-[var(--accent)] text-white text-[13px] font-medium rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors flex items-center justify-center gap-2 shrink-0">
                  Fill & Preview <ChevronRight size={14} />
                </button>
              )}
            </div>
          )}

          {step === 3 && selEmployee && selTemplate && (
            <div className="flex flex-1 overflow-hidden">
              {/* Sidebar summary */}
              <div className="w-56 border-r border-[var(--border)] bg-[var(--bg-card)] p-3 shrink-0 flex flex-col gap-3">
                <button onClick={goBack} className="text-[11px] text-[var(--accent)] hover:underline text-left">← Change template</button>
                <div>
                  <p className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-1">Employee</p>
                  <p className="text-[12px] font-medium text-[var(--text-primary)]">{`${selEmployee.first_name || ''} ${selEmployee.last_name || ''}`.trim()}</p>
                  <p className="text-[10px] text-[var(--text-tertiary)]">{selEmployee.employee_code}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-1">Template</p>
                  <p className="text-[12px] font-medium text-[var(--text-primary)]">{selTemplate.name}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-1">Auto-filled</p>
                  <p className="text-[11px] text-[var(--text-secondary)]">{selTemplate.variables?.length || 0} variables resolved from employee profile</p>
                </div>
              </div>
              {/* Document editor */}
              <div className="flex-1 overflow-hidden">
                <DocumentEditor employee={selEmployee} template={selTemplate} company={company} user={user} />
              </div>
            </div>
          )}

          {/* Empty state for step 1 after employee selected but not moved yet */}
          {step === 1 && !selEmployee && (
            <div className="flex-1 flex items-center justify-center text-[var(--text-tertiary)]">
              <div className="text-center">
                <FileText size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-[14px] font-medium">Select an employee to get started</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
